import React from 'react';
import {
  CheckCircle,
  Apple,
  Smartphone,
  Phone,
  Laptop,
  Bot,
  HelpCircle,
  Trash2,
} from 'lucide-react';

import {useAppSelector} from '../state/hooks';
import {useBrowserId} from '../BrowserId';
import {useMyDevices} from '../net/query/MyDevices.gql';
import {DeviceType, type Device} from '../net/types/graphql';
import {useUnregisterDevice} from '../net/query/UnregisterDevice.gql';
import {Button} from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {cn} from '../lib/utils';

const getDeviceIcon = (t: DeviceType) => {
  switch (t) {
    case DeviceType.Ios:
      return <Apple className="w-4 h-4" />;
    case DeviceType.Android:
      return <Smartphone className="w-4 h-4" />;
    case DeviceType.Mobile:
      return <Phone className="w-4 h-4" />;
    case DeviceType.Web:
      return <Laptop className="w-4 h-4" />;
    case DeviceType.Rpc:
      return <Bot className="w-4 h-4" />;
    default:
      return <HelpCircle className="w-4 h-4" />;
  }
};

/**
 * Device list and options.
 */
export const ManageDevices = () => {
  const thisDeviceId = useBrowserId();
  const [unregister] = useUnregisterDevice();
  const userId = useAppSelector(({auth}) => auth.userId!);
  const {data, loading, error} = useMyDevices({
    variables: {
      id: userId,
    },
  });

  if (error) {
    return <div className="text-destructive p-4">Error: {error.message}</div>;
  }

  if (loading) {
    return (
      <div className="p-4 text-color-(--muted-foreground)">
        Loading devices...
      </div>
    );
  }

  const devices = (
    (data?.node?.__typename === 'StaffAccount' && data.node.devices) ||
    []
  ).slice();

  devices.sort((a: Device, b: Device) =>
    a.id === thisDeviceId ? -1 : b.id === thisDeviceId ? 1 : 0,
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Current</TableHead>
            <TableHead className="w-16">Type</TableHead>
            <TableHead>Device ID</TableHead>
            <TableHead className="w-16">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device: Device) => (
            <TableRow
              key={device.address!}
              className={cn(
                device.id === thisDeviceId && 'bg-muted border-primary',
              )}
            >
              <TableCell className="text-center">
                {device.id === thisDeviceId ? (
                  <CheckCircle className="w-4 h-4 text-color-(--constructive) mx-auto" />
                ) : null}
              </TableCell>
              <TableCell className="text-center">
                {getDeviceIcon(device.type)}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.address}
              </TableCell>
              <TableCell className="text-center">
                {thisDeviceId === device.id ? (
                  <span className="text-xs text-color-(--muted-foreground)">
                    Current device
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                    onClick={() =>
                      unregister({variables: {deviceId: device.id}})
                    }
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
